import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check } from 'lucide-react';
import {
  useGetOnboardingQuery,
  useUpdateOnboardingMutation,
} from '../../../store/api/onboardingApi';
import { STEPS, stepIndex, PREPARING_PATH } from '../steps';

// Where each plan's button leads once onboarding is saved. Premium is bought on
// the Billing page; Enterprise is a conversation, so it opens the website's
// contact page. Free goes through the usual preparing screen.
const BILLING_PATH = '/dashboard/billing';
const WEBSITE_URL = (process.env.REACT_APP_WEBSITE_URL || 'https://maxelpay.com').replace(/\/+$/, '');
const CONTACT_URL = `${WEBSITE_URL}/contact`;

const PLAN_STEP = stepIndex('plan') + 1;
const PLAN_PCT = Math.round((PLAN_STEP / STEPS.length) * 100);

const ProgressHeader = () => (
  <div className="ob-progress">
    <div className="ob-progress-meta">
      <span className="ob-progress-step">
        Step {PLAN_STEP} of {STEPS.length}
      </span>
      <span className="ob-progress-pct">{PLAN_PCT}% complete</span>
    </div>
    <div className="ob-progress-track">
      <div className="ob-progress-fill" style={{ width: `${PLAN_PCT}%` }} />
    </div>
  </div>
);

/**
 * Plan step. Additive to the onboarding state machine: lets the merchant pick a
 * plan, persists it via the existing onboarding client (`plan` field), marks
 * `onboarding_status: 'completed'`, and hands off to the preparing screen.
 * All three choices advance the flow; the selection is non-blocking and the
 * merchant can change plans later from Billing.
 */
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For testing and small merchants',
    price: '0',
    priceSuffix: '/ month',
    fee: '2.0%',
    feeSave: null,
    cta: 'Continue with Free',
    ctaClass: 'rd-btn-secondary',
    featuresHeading: 'INCLUDES',
    features: [
      { text: '5 chains: BTC, ETH, Tron, BSC, Polygon' },
      { text: '10 stablecoins + native assets' },
      { text: 'Hosted checkout (MaxelPay branded)' },
      { text: 'Standard webhooks' },
      { text: 'Email support · 48hr response' },
      { text: '1 team seat' },
      { text: '$1 min payout · $2 ETH min' },
      { text: 'Custom branded checkout', muted: true },
      { text: 'Custom token requests', muted: true },
      { text: 'Advanced analytics', muted: true },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'For growing merchants ready to scale',
    price: '49',
    priceSuffix: '/ month',
    fee: '1.0%',
    feeSave: 'save 50%',
    cta: 'Upgrade to Premium',
    ctaClass: 'rd-btn-purple',
    ctaNote: 'No charge until day 15 · Cancel anytime',
    recommended: true,
    featuresHeading: 'EVERYTHING IN FREE, PLUS',
    features: [
      { text: '12 chains incl. Solana, Base, Arbitrum, Optimism, Avalanche, TON, Sui' },
      { text: '50+ tokens including memecoins (SHIB, PEPE, BONK)' },
      { text: 'Custom branded checkout with your logo & colors' },
      { text: 'Custom token requests · 5-day SLA' },
      { text: 'Advanced analytics & cohort reports' },
      { text: 'Webhook SLA · auto-retry up to 168hrs' },
      { text: 'Priority support · 4hr response' },
      { text: '5 team seats with roles' },
      { text: '$0.50 min payout · $1 ETH min' },
      { text: '10x API rate limits' },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For $1M+ monthly volume',
    price: 'Custom',
    priceIsText: true,
    feeLabel: 'Volume-based fee',
    fee: 'as low as 0.5%',
    cta: 'Talk to sales',
    ctaClass: 'rd-btn-secondary',
    featuresHeading: 'EVERYTHING IN PREMIUM, PLUS',
    features: [
      { text: 'All chains + custom integration requests' },
      { text: 'Dedicated wallet infrastructure' },
      { text: '24/7 support with on-call SLA' },
      { text: 'Dedicated account manager' },
      { text: 'SOC 2 documentation' },
      { text: 'Custom contracts & MSA' },
      { text: 'Unlimited team seats' },
      { text: 'White-label option' },
    ],
  },
];

const Plan = () => {
  const navigate = useNavigate();
  const { data: state, isLoading } = useGetOnboardingQuery();
  const [updateOnboarding, { isLoading: isSaving }] = useUpdateOnboardingMutation();
  const [pending, setPending] = useState(null);

  useEffect(() => {
    // Prefill is not needed for action-only cards, but keep the saved plan
    // available if the merchant returns; nothing to set locally otherwise.
  }, [state]);

  const choose = async (planId) => {
    // Opened on the click itself: a tab opened after the await below is no
    // longer tied to a user gesture and popup blockers refuse it.
    if (planId === 'enterprise') {
      window.open(CONTACT_URL, '_blank', 'noopener,noreferrer');
    }

    setPending(planId);
    try {
      await updateOnboarding({
        plan: planId,
        onboarding_status: 'completed',
        // Finishing and "Save & exit" both mean "do not show me this again",
        // so both set the same flag and resume only has one thing to check.
        onboarding_dismissed: true,
      }).unwrap();
      // Last step. Premium goes straight to Billing, where the upgrade is
      // actually paid; everyone else (Enterprise included -- their contact tab
      // is already open) goes through the preparing screen to the dashboard.
      navigate(planId === 'premium' ? BILLING_PATH : PREPARING_PATH);
    } catch (err) {
      setPending(null);
      toast.error(err?.message || 'Could not save your plan. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <section className="wiz-plan" aria-busy="true">
        <div className="wiz-skeleton" style={{ width: '40%', height: 28, margin: '0 auto 12px' }} />
        <div className="wiz-skeleton" style={{ width: '60%', margin: '0 auto 32px' }} />
        <div className="wiz-plan-grid">
          {PLANS.map((p) => (
            <div key={p.id} className="wiz-skeleton block" style={{ height: 320 }} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="wiz-plan" aria-label="Choose your plan">
      <ProgressHeader />
      <header className="wiz-plan-head">
        <h1 className="wiz-plan-title">Choose your plan</h1>
        <p className="wiz-plan-sub">
          Start free. Upgrade when you&apos;re ready. Cancel anytime.
        </p>
      </header>

      <div className="wiz-plan-grid">
        {PLANS.map((plan) => {
          const isBusy = pending === plan.id || (isSaving && pending === plan.id);
          return (
            <div
              key={plan.id}
              className={`rd-card wiz-plan-card ${plan.recommended ? 'recommended' : ''}`}
            >
              {plan.recommended && (
                <span className="rd-badge accent wiz-plan-pill">Recommended for you</span>
              )}

              <div className="wiz-plan-card-head">
                <h2 className="wiz-plan-name">{plan.name}</h2>
                <p className="wiz-plan-tagline">{plan.tagline}</p>
              </div>

              <div className="wiz-plan-price">
                {plan.priceIsText ? (
                  <span className="wiz-plan-amount text">{plan.price}</span>
                ) : (
                  <>
                    <span className="wiz-plan-currency">$</span>
                    <span className="wiz-plan-amount">{plan.price}</span>
                    <span className="wiz-plan-period">{plan.priceSuffix}</span>
                  </>
                )}
              </div>

              <div className="wiz-plan-fee">
                <span className="wiz-plan-fee-label">
                  {plan.feeLabel || 'Transaction fee:'}
                </span>{' '}
                <span className="wiz-plan-fee-value">{plan.fee}</span>
                {plan.feeSave && <span className="wiz-plan-fee-save"> · {plan.feeSave}</span>}
              </div>

              <button
                type="button"
                className={`rd-btn ${plan.ctaClass} rd-btn-block wiz-plan-cta`}
                onClick={() => choose(plan.id)}
                disabled={isSaving}
              >
                {isBusy ? <span className="loading-spinner" /> : plan.cta}
              </button>
              {plan.ctaNote && <p className="wiz-plan-cta-note">{plan.ctaNote}</p>}

              <div className="wiz-plan-features">
                <p className="wiz-plan-features-heading">{plan.featuresHeading}</p>
                <ul>
                  {plan.features.map((f, i) => (
                    <li key={i} className={f.muted ? 'muted' : ''}>
                      <Check size={15} strokeWidth={3} aria-hidden="true" />
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <div className="wiz-plan-foot">
        <button type="button" className="wiz-link" onClick={() => navigate('/onboarding/use-cases')}>
          Back
        </button>
      </div>
    </section>
  );
};

export default Plan;
